/*eslint-disable @typescript-eslint/explicit-module-boundary-types*/

import FlattenUtil from 'flat';
import { MerchantOnboardingBusinessAddressRequest, MerchantResponseDto, OnboardingRequest, SessionPrefillData } from "@/api/paymentApi";
import { AppDefaults } from "@/app/utils/common/constants";
import { stateCode } from "@/app/validators/countrySpecificValidationSpec";
import { OnboardingSectionData } from "@/store/modules/onboarding/onboardingTypes";
import { copyFromApiResponse, copyFromErrorResponse, prefillField } from "@/store/storeMappers";
import { validateAndGenerateError, validateAndSetValid } from "@/store/validationFromStore.js";

export enum BusinessAddressFields {
    COUNTRY ="country",
    LINE1 ="line1",
    LINE2 ="line2",
    CITY ="city",
    STATE ="state_code",
    ZIP ="postal_code",
}

export const incrementalOnboardingBusinessAddressState: OnboardingSectionData = {
    api: "createMerchant",
    errors: {},
    dirty: false,
    isPrefilled: false,
    data: {
        [BusinessAddressFields.COUNTRY]: {
            apiPath: "business_info.address.country",
            value: AppDefaults.country,
            valid: false,
        },
        [BusinessAddressFields.LINE1]: {
            apiPath: "business_info.address.line1",
            value: null,
            valid: false
        },
        [BusinessAddressFields.LINE2]: {
            apiPath: "business_info.address.line2",
            value: null,
            valid: false
        },
        [BusinessAddressFields.CITY]: {
            apiPath: "business_info.address.city",
            value: null,
            valid: false
        },
        [BusinessAddressFields.STATE]: {
            apiPath: "business_info.address.state_code",
            value: null,
            valid: false,
            validationSpec: stateCode
        },
        [BusinessAddressFields.ZIP]: {
            apiPath: "business_info.address.postal_code",
            formattedValue: undefined,
            value: null,
            valid: false
        },
    },
};

const getters = {
    isValid: (state) => {
        return Object.keys(state.data).every(key => state.data[key].valid);
    },
    getErrors: (state) => {
        return state.errors
    },
    isPrefilled: (state) => {
        return state.isPrefilled
    },
    totalInvalidFields: (s) => {
        return Object.keys(s.data).filter(key => s.data[key].valid === false).length
    },
    totalValidFields: (s) => {
        return Object.keys(s.data).filter(key => s.data[key].valid === true).length
    },
    getCountry: s => s.data[BusinessAddressFields.COUNTRY].value,
    getField: (s) => {
        return (field: BusinessAddressFields) => s.data[field].value 
    },
    isDirty: (s) => {
        return s.dirty
    },
    getFieldFormatted: (s) => {
        return (field: BusinessAddressFields) => s.data[field].formattedValue
    },
    getApiPathOfField: (s) => {
        return (field: BusinessAddressFields) => s.data[field].apiPath;
    },
    getIncrementalOnboardingData: (s): MerchantOnboardingBusinessAddressRequest => {
        return {
            requestType: OnboardingRequest.RequestTypeEnum.UPDATEBUSINESSADDRESS,
            line1: s.data[BusinessAddressFields.LINE1].value,
            line2: s.data[BusinessAddressFields.LINE2].value,
            city: s.data[BusinessAddressFields.CITY].value,
            state_code: s.data[BusinessAddressFields.STATE].value,
            postal_code: s.data[BusinessAddressFields.ZIP].value
        };
    }
}

const actions = {
    resetErrors({commit}) {
        commit('resetErrors');
    },
    setErrors({commit}, {key, errors}) {
        commit('setErrors', {key, errors});
    },
    validate({commit, state, rootGetters}) {
        commit('resetErrors')
        return validateAndGenerateError(commit, state.api, rootGetters, rootGetters["essentials/getCountry"], state.data)
    },
    validateField({commit, state, rootGetters}, key) {
        validateAndSetValid(commit, incrementalOnboardingBusinessAddressState.api, state.data, rootGetters["essentials/getCountry"], key)
    },
    validateAllFields({state, dispatch}) {
        Object.keys(state.data).forEach(key => dispatch('validateField', key))
    },
    resetDirty({commit}) {
        commit('resetDirty')
    },
    updateField({commit, dispatch}, {key, event}: {key: BusinessAddressFields, event: any}) {
        commit('updateField', {key, value: event.target.value})
        commit('setDirty')
        dispatch('validateField', key)
    },
    updateFieldFromEvent({commit, dispatch}, {key, event}: {key: BusinessAddressFields, event: any}) {
        commit('updateField', {key, value: event.target.value})
        commit('setDirty')
        dispatch('validateField', key)
    },
    updateFieldFromValue({commit, dispatch}, {key, value}: {key: BusinessAddressFields, value: any}) {
        commit('updateField', {key, value})
        commit('setDirty')
        dispatch('validateField', key)
    },
    copyFromFieldErrorsResponse({commit}, subErrors) {
        commit('copyFromFieldErrorsResponse', subErrors)
    },
    copyFromMerchantApiResponse({commit}, merchantApiResponse) {
        commit('copyFromMerchantApiResponse', merchantApiResponse)
    },
    copyFromPrefillData({commit}, {prefillData, merchantResponse}) {
        commit('copyFromPrefillData', {prefillData, merchantResponse})
    }
};

const mutations = {
    resetErrors(state): void {
        state.errors = {}
    },
    setErrors(state, {key, errors}): void {
        state.errors[key] = errors;
    },
    setInvalid(state, key) {
        state.data[key].valid = false;
    },
    setValid(state, key) {
        state.data[key].valid = true;
    },
    setDirty(state) {
        state.dirty = true;
    },
    resetDirty(state) {
        state.dirty = false;
    },
    updateField(state, {key, value}) {
        state.data[key].value = value;
        delete state.errors[key];
    },
    copyFromFieldErrorsResponse: function (state, subErrors): void {
        state.errors = copyFromErrorResponse(subErrors, state.data)
    },
    copyFromMerchantApiResponse(state, merchantApiResponse): void {
        state.data = copyFromApiResponse(merchantApiResponse, state.data);
    },
    copyFromPrefillData(state, {prefillData, merchantResponse}: {prefillData: SessionPrefillData, merchantResponse: MerchantResponseDto}): void {
        const flatApiResponse = FlattenUtil(merchantResponse || {});
        let prefillApplied = false;
        
        
        prefillApplied = prefillField(state, BusinessAddressFields.COUNTRY, flatApiResponse, prefillData.organization?.address?.country) || prefillApplied;
        prefillApplied = prefillField(state, BusinessAddressFields.STATE, flatApiResponse, prefillData.organization?.address?.state_code) || prefillApplied;
        prefillApplied = prefillField(state, BusinessAddressFields.ZIP, flatApiResponse, prefillData.organization?.address?.postal_code) || prefillApplied;
        prefillApplied = prefillField(state, BusinessAddressFields.CITY, flatApiResponse, prefillData.organization?.address?.city) || prefillApplied;
        prefillApplied = prefillField(state, BusinessAddressFields.LINE1, flatApiResponse, prefillData.organization?.address?.line1) || prefillApplied;
        prefillApplied = prefillField(state, BusinessAddressFields.LINE2, flatApiResponse, prefillData.organization?.address?.line2) || prefillApplied;

        state.isPrefilled = prefillApplied;
    }
};

export const incrementalOnboardingBusinessAddress = {
    namespaced: true,
    state: incrementalOnboardingBusinessAddressState,
    getters,
    actions,
    mutations,
};
